import { map, mapObjIndexed, merge, prop, reduce, sortBy } from "ramda";
import type { DefaultRootState } from "react-redux";
import { createSelector } from "reselect";
import { sumDGItemCount } from "group_management/helpers/reconcilers";
import type {
  ICategory,
  ICategoryHydrated,
  ICourse,
  ICourseHydrated,
  IItem,
  IItemRecord,
  IMenu,
  IMenuRecord,
  IModGroup,
  IModGroupRecord,
  IModifier,
  IModifierRecord,
  ISection,
  ISectionRecord,
  ITaxRate,
  ITaxRateCounted,
} from "group_management/types";
import { findById, mostCommonBy } from "helpers/collections";

import { storeNamesSelector } from "selectors/context";

export const groupManagementSelector = (state: DefaultRootState) =>
  state.groupManagement;

export const menuRecordsSelector = createSelector(
  groupManagementSelector,
  prop<"menus">("menus")
);
export const sectionRecordsSelector = createSelector(
  groupManagementSelector,
  prop<"sections">("sections")
);
export const itemRecordsSelector = createSelector(
  groupManagementSelector,
  prop<"items">("items")
);
export const categoriesSelector = createSelector(
  groupManagementSelector,
  prop<"categories">("categories")
);
export const coursesSelector = createSelector(
  groupManagementSelector,
  prop<"courses">("courses")
);
export const modifierRecordsSelector = createSelector(
  groupManagementSelector,
  prop<"modifiers">("modifiers")
);
export const modifierGroupRecordsSelector = createSelector(
  groupManagementSelector,
  prop<"modifierGroups">("modifierGroups")
);
export const printerGroupsSelector = createSelector(
  groupManagementSelector,
  prop<"printerGroups">("printerGroups")
);
export const taxRatesSelector = createSelector(
  groupManagementSelector,
  prop<"taxRates">("taxRates")
);
export const requestStatusSelector = createSelector(
  groupManagementSelector,
  prop<"requestStatus">("requestStatus")
);

// HYDRATED RECORDS
export const sectionsSelector = createSelector(
  sectionRecordsSelector,
  storeNamesSelector,
  (sections, storeNames) =>
    mapObjIndexed<ISectionRecord[], ISection[]>((theseSections, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<ISectionRecord, ISection>(mergeStoreDetails, theseSections);
    }, sections)
);

export const menusSelector = createSelector(
  menuRecordsSelector,
  sectionsSelector,
  storeNamesSelector,
  (menus, sectionsLookup, storeNames) =>
    mapObjIndexed<IMenuRecord[], IMenu[]>((theseMenus, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });

      return map<IMenuRecord, IMenu>((thisMenu) => {
        const sectionsAtThisRestaurant = sectionsLookup[storeId] || [];

        // map ISectionReference to the actual ISection record, if found
        const sections = reduce(
          (acc, { sectionId }) => {
            const match = findById(sectionId, sectionsAtThisRestaurant);
            if (match) {
              return acc.concat(match);
            }
            return acc;
          },
          [] as ISection[],
          sortBy(prop("order"), thisMenu.menuSections)
        );

        return mergeStoreDetails({
          ...thisMenu,
          itemsCount: sumDGItemCount(sections),
          sections,
        });
      }, theseMenus);
    }, menus)
);

export const itemsSelector = createSelector(
  itemRecordsSelector,
  storeNamesSelector,
  (items, storeNames) =>
    mapObjIndexed<IItemRecord[], IItem[]>((theseItems, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<IItemRecord, IItem>(mergeStoreDetails, theseItems);
    }, items)
);

export const modifierGroupsSelector = createSelector(
  modifierGroupRecordsSelector,
  storeNamesSelector,
  (modifierGroups, storeNames) =>
    mapObjIndexed<IModGroupRecord[], IModGroup[]>((theseModGroups, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<IModGroupRecord, IModGroup>(mergeStoreDetails, theseModGroups);
    }, modifierGroups)
);

export const modifiersSelector = createSelector(
  modifierRecordsSelector,
  storeNamesSelector,
  (modifiers, storeNames) =>
    mapObjIndexed<IModifierRecord[], IModifier[]>((theseModifiers, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<IModifierRecord, IModifier>(mergeStoreDetails, theseModifiers);
    }, modifiers)
);

// Additive hydrated variants: categoriesSelector/coursesSelector return raw
// records and are consumed as lookups elsewhere, so they keep their shape.
export const categoriesHydratedSelector = createSelector(
  categoriesSelector,
  storeNamesSelector,
  (categories, storeNames) =>
    mapObjIndexed<ICategory[], ICategoryHydrated[]>(
      (theseCategories, storeId) => {
        const mergeStoreDetails = merge({
          storeId,
          storeName: storeNames[storeId],
        });
        return map<ICategory, ICategoryHydrated>(
          mergeStoreDetails,
          theseCategories
        );
      },
      categories
    )
);

export const coursesHydratedSelector = createSelector(
  coursesSelector,
  storeNamesSelector,
  (courses, storeNames) =>
    mapObjIndexed<ICourse[], ICourseHydrated[]>((theseCourses, storeId) => {
      const mergeStoreDetails = merge({
        storeId,
        storeName: storeNames[storeId],
      });
      return map<ICourse, ICourseHydrated>(mergeStoreDetails, theseCourses);
    }, courses)
);

export const taxRatesCountedSelector = createSelector(
  taxRatesSelector,
  itemsSelector,
  (taxRates, items) =>
    mapObjIndexed<ITaxRate[], ITaxRateCounted[]>((theseTaxRates, storeId) => {
      const itemsAtStore = items[storeId] || [];
      return theseTaxRates.map<ITaxRateCounted>((taxRate) => {
        const itemsHavingTaxRate = itemsAtStore.filter(
          ({ taxRates: itemTaxRates }) => itemTaxRates.includes(taxRate.id)
        );
        return merge(
          {
            count: itemsHavingTaxRate.length,
            label: `${taxRate.name} ${taxRate.displayPercent}`,
          },
          taxRate
        );
      });
    }, taxRates)
);

export const mostCommonCoursesSelector = createSelector(
  itemRecordsSelector,
  (items) =>
    // For each store location, find the most common course by counting items.
    mapObjIndexed<IItemRecord[], string>(
      (theseItems) => mostCommonBy(prop("courseId"), theseItems),
      items
    )
);
